'use client';

import AutoPlay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import {
  type ComponentPropsWithoutRef,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { cn } from '../../lib/utils';
import type {
  DesktopAspectRatio,
  MobileAspectRatio,
} from '../../sanity/studio/constants';
import { AspectRatioMobileDesktop } from '../aspect-ratio';
import { Container } from '../container';
import { CustomLink } from '../custom-link';
import { ModuleWrapper, type ModuleWrapperProps } from '../module-wrapper';

type Settings =
  | {
      type: 'aspectRatio';
      mobileAspectRatio: MobileAspectRatio;
      desktopAspectRatio: DesktopAspectRatio;
    }
  | {
      type: 'moduleWrapper';
      moduleWrapper: ModuleWrapperProps;
    };

export interface HeroModuleProps {
  children: ReactNode[];
  settings: Settings;
  className?: string;
}

export function HeroModuleShared({
  children,
  settings,
  className,
}: HeroModuleProps) {
  if (!children?.length) return null;

  const hasOnlyOneItem = children.length === 1;

  if (hasOnlyOneItem) {
    return (
      <SingleHeroItem
        content={children[0]}
        settings={settings}
        className={className}
      />
    );
  }

  return (
    <CarouselHeroItems
      content={children}
      settings={settings}
      className={className}
    />
  );
}

function SingleHeroItem({
  content,
  settings,
  className,
}: {
  content: ReactNode;
  settings: Settings;
  className?: string;
}) {
  if (settings.type === 'aspectRatio') {
    return (
      <AspectRatioMobileDesktop
        aspectRatioMobile={settings.mobileAspectRatio}
        aspectRatioDesktop={settings.desktopAspectRatio}
        className={cn(className, 'relative w-full')}
      >
        {content}
      </AspectRatioMobileDesktop>
    );
  }
  return (
    <ModuleWrapper
      {...settings.moduleWrapper}
      className={cn(className, 'h-fit')}
    >
      {content}
    </ModuleWrapper>
  );
}

function CarouselHeroItems({
  content,
  settings,
  className,
}: {
  content: ReactNode[];
  settings: Settings;
  className?: string;
}) {
  const autoplayOptions = {
    delay: 5000,
    stopOnInteraction: false,
    stopOnMouseEnter: false,
    playOnInit: true,
  };
  const [autoplay] = useState(() => AutoPlay(autoplayOptions));
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [autoplay]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isHydrated, setIsHydrated] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    emblaApi.on('select', onSelect);
    onSelect();

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  // Set hydrated flag after component mounts to prevent hydration mismatch
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const scrollTo = useCallback(
    (index: number) => {
      if (!emblaApi) return;

      // Stop and reset autoplay when user manually navigates
      autoplay.stop();
      emblaApi.scrollTo(index);

      // Reset autoplay after a brief delay
      setTimeout(() => {
        autoplay.reset();
        autoplay.play();
      }, 50);
    },
    [emblaApi, autoplay]
  );

  return (
    <div className={cn(className, 'relative')}>
      {settings.type === 'aspectRatio' ? (
        <AspectRatioMobileDesktop
          aspectRatioMobile={settings.mobileAspectRatio}
          aspectRatioDesktop={settings.desktopAspectRatio}
          className="w-full"
        >
          <div className="h-full w-full overflow-hidden" ref={emblaRef}>
            <div className="flex h-full">
              {content.map((child, index) => (
                <div key={index} className="relative h-full flex-[0_0_100%]">
                  <div className="relative h-full w-full">{child}</div>
                </div>
              ))}
            </div>
          </div>
        </AspectRatioMobileDesktop>
      ) : (
        <ModuleWrapper {...settings.moduleWrapper} className={className}>
          <div className="h-full w-full overflow-hidden" ref={emblaRef}>
            <div className="flex h-full">{content}</div>
          </div>
        </ModuleWrapper>
      )}

      {isHydrated && (
        <div className="-translate-x-1/2 absolute bottom-6 left-1/2 z-[3] grid auto-cols-min grid-flow-col-dense items-center justify-center gap-3 text-white">
          {content.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => scrollTo(index)}
              className={cn('group -m-1 p-1')}
              aria-label={`Go to slide ${index + 1}`}
            >
              <span className="sr-only">Show Slide {index + 1}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3 w-3"
                viewBox="0 0 30 30"
                xmlSpace="preserve"
              >
                <title className="sr-only">Show Slide {index + 1}</title>
                <circle
                  cx="15"
                  cy="15"
                  r="13"
                  fill="currentColor"
                  strokeWidth="4"
                  strokeDasharray="90"
                  className={cn(
                    '-rotate-90 origin-center',
                    selectedIndex === index ? 'animate-dot' : ''
                  )}
                  style={{ animationDuration: '5s' }}
                />
                <circle
                  cx="15"
                  cy="15"
                  r="13"
                  fill="transparent"
                  stroke="currentColor"
                  strokeWidth="4"
                  strokeDasharray="90"
                  className={cn(
                    '-rotate-90 origin-center',
                    selectedIndex === index ? 'animate-circle' : ''
                  )}
                  style={{ animationDuration: '5s' }}
                />
              </svg>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function HeroWrapper({
  children,
  href,
  target,
  ...props
}: ComponentPropsWithoutRef<typeof CustomLink> & {
  children: ReactNode;
  href: string;
  target: string;
}) {
  return (
    <CustomLink href={href} target={target} {...props}>
      {children}
    </CustomLink>
  );
}

function HeroContent({
  hasBackgroundAsset,
  children,
  className,
  verticalAlignmentMobile,
  verticalAlignmentDesktop,
}: {
  hasBackgroundAsset: boolean;
  children: ReactNode;
  className?: string;
  verticalAlignmentMobile: 'top' | 'center' | 'bottom';
  verticalAlignmentDesktop: 'top' | 'center' | 'bottom';
}) {
  return (
    <Container
      className={cn(
        'flex h-full min-h-0 w-full flex-col p-10',
        verticalAlignmentMobile === 'top' && 'justify-start',
        verticalAlignmentMobile === 'center' && 'justify-center',
        verticalAlignmentMobile === 'bottom' && 'justify-end',
        verticalAlignmentDesktop === 'top' && 'lg:justify-start',
        verticalAlignmentDesktop === 'center' && 'lg:justify-center',
        verticalAlignmentDesktop === 'bottom' && 'lg:justify-end',
        hasBackgroundAsset && 'absolute inset-0 z-[1]',
        className
      )}
    >
      <div className="flex flex-col items-center justify-center gap-8">
        {children}
      </div>
    </Container>
  );
}

// function HeroContent({
//   hasBackgroundAsset,
//   children,
//   className,
// }: {
//   hasBackgroundAsset: boolean;
//   children: ReactNode;
//   className?: string;
// }) {
//   return (
//     <Container
//       className={cn(
//         'grid h-fit place-items-center gap-8 self-center',
//         hasBackgroundAsset && 'absolute inset-0 z-[1]',
//         className
//       )}
//     >
//       {children}
//     </Container>
//   );
// }

function HeroOverlay() {
  return (
    <div className="absolute inset-0 z-[2] bg-gradient-to-b from-black/5 via-black/5 to-black/30" />
  );
}

HeroModuleShared.Wrapper = HeroWrapper;
HeroModuleShared.Overlay = HeroOverlay;
HeroModuleShared.Content = HeroContent;
